import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileImage, AlertCircle, Loader2 } from 'lucide-react';
import Session from 'supertokens-web-js/recipe/session';

const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']);
const MAX_BYTES = 10 * 1024 * 1024;

export default function AdminCustomsUpload() {
  const [files, setFiles] = useState<File[]>([]);
  const [destination, setDestination] = useState('US');
  const [origin, setOrigin] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  function handleFiles(selected: FileList | null) {
    if (!selected) return;
    const next: File[] = [];
    for (const f of Array.from(selected)) {
      if (!ALLOWED.has(f.type)) { setError(`Unsupported file: ${f.name}`); return; }
      if (f.size > MAX_BYTES) { setError(`${f.name} exceeds 10MB`); return; }
      next.push(f);
    }
    setError(null);
    setFiles(next);
  }

  async function submit() {
    if (files.length === 0) return setError('Select at least one file');
    if (!/^[A-Za-z]{2}$/.test(destination)) return setError('Destination must be ISO 3166-1 alpha-2');
    setSubmitting(true);
    setError(null);
    try {
      const form = new FormData();
      for (const f of files) form.append('files', f);
      form.append('destination_country', destination.toUpperCase());
      if (origin) form.append('origin_country', origin.toUpperCase());
      if (supplierId) form.append('supplier_id', supplierId);
      const headers: Record<string, string> = {};
      if (await Session.doesSessionExist()) {
        const t = await Session.getAccessToken();
        if (t) headers.Authorization = `Bearer ${t}`;
      }
      const apiUrl = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';
      const resp = await fetch(`${apiUrl}/admin/customs/invoices`, {
        method: 'POST',
        headers,
        body: form,
        credentials: 'include',
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      navigate(`/admin/customs/${data.invoice_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-xl font-semibold mb-2">Upload Customs Invoice</h1>
      <p className="text-gray-600 text-sm mb-6">
        Forward to ETaxEngine for HS code classification.
      </p>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed rounded-lg p-10 text-center cursor-pointer hover:bg-gray-50"
      >
        <Upload className="w-10 h-10 mx-auto mb-2 text-gray-400" />
        <p className="font-medium">Drop invoice files or click to select</p>
        <p className="text-sm text-gray-500">JPEG / PNG / WEBP / PDF, up to 10MB</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      {files.length > 0 && (
        <ul className="mt-3 text-sm text-gray-700 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center"><FileImage className="w-4 h-4 mr-2 text-gray-400" />{f.name}</li>
          ))}
        </ul>
      )}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <label className="block"><span className="text-sm font-medium">Destination</span>
          <input value={destination} onChange={(e) => setDestination(e.target.value)} maxLength={2} className="mt-1 block w-full rounded border-gray-300" />
        </label>
        <label className="block"><span className="text-sm font-medium">Origin</span>
          <input value={origin} onChange={(e) => setOrigin(e.target.value)} maxLength={2} className="mt-1 block w-full rounded border-gray-300" />
        </label>
        <label className="block"><span className="text-sm font-medium">Supplier ID (opt.)</span>
          <input value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="mt-1 block w-full rounded border-gray-300" />
        </label>
      </div>
      {error && (
        <div className="mt-3 flex items-start gap-2 p-3 rounded bg-red-50 text-red-800 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5" />{error}
        </div>
      )}
      <button
        onClick={submit}
        disabled={submitting || files.length === 0}
        className="mt-6 inline-flex items-center px-4 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400"
      >
        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Classify
      </button>
    </div>
  );
}
